import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/* Minimal shared shell — header + footer — for content routes (insights / dealers / quote). */

export const PlatformShellHeader = () => (
  <header
    data-testid="shell-nav"
    className="sticky top-0 left-0 right-0 z-50 bg-[#0F1113]/90 backdrop-blur-xl border-b border-white/5"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" data-testid="shell-logo">
        <div className="relative w-9 h-9 border border-white/15 flex items-center justify-center bg-[#16191C]">
          <span className="font-display text-white font-black text-sm tracking-tight">BBC</span>
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#B0FFD9] bbc-pulse" />
        </div>
        <div className="leading-tight">
          <div className="font-display text-white text-[13px] font-bold tracking-wide">Boom Barrier Co.</div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A]">Infrastructure Systems</div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-7">
        <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition" data-testid="shell-nav-platform">Platform</Link>
        <Link to="/insights" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition" data-testid="shell-nav-insights">Insights</Link>
        <Link to="/quote" className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition" data-testid="shell-nav-quote">Get a Quote</Link>
      </nav>
      <Link
        to="/quote"
        data-testid="shell-cta"
        className="inline-flex items-center gap-2 bg-[#00FF85] hover:bg-[#00CC6A] text-black font-semibold px-4 py-2.5 text-[11px] uppercase tracking-wider transition"
      >
        Talk to Sales
      </Link>
    </div>
  </header>
);

export const PlatformShellFooter = () => (
  <footer data-testid="shell-footer" className="border-t border-white/5 bg-[#0F1113] mt-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-3 gap-8">
      <div>
        <div className="font-display text-white text-lg font-bold tracking-tight">Boom Barrier Co.</div>
        <p className="mt-3 text-[#A1A1AA] text-sm font-body max-w-sm leading-relaxed">
          India's first traceable automatic boom barrier brand. Made in India. Tracked from your phone.
        </p>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Explore</div>
        <ul className="mt-3 space-y-2 text-sm">
          <li><Link to="/" className="text-white/80 hover:text-white">Platform Overview</Link></li>
          <li><Link to="/insights" className="text-white/80 hover:text-white">Insights & Guides</Link></li>
          <li><Link to="/products" className="text-white/80 hover:text-white">Product Portfolio</Link></li>
          <li><Link to="/#partners" className="text-white/80 hover:text-white">For Dealers & Enterprises</Link></li>
        </ul>
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">Get in touch</div>
        <ul className="mt-3 space-y-2 text-sm">
          <li><Link to="/quote" className="text-white/80 hover:text-white">Request a Quote</Link></li>
          <li><a href="/#partners" className="text-white/80 hover:text-white">Become a Dealer</a></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4 text-[10px] font-mono uppercase tracking-[0.25em] text-[#52525B]">
        <span>© Boom Barrier Co. · Made in India</span>
        <span>boombarriercompany.com</span>
      </div>
    </div>
  </footer>
);

/* SEO helper — imperatively sets head tags per page. */
export const useSeo = ({ title, description, canonical, image, jsonLd }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    const setProp = (prop, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", prop);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("description", description);
    setProp("og:title", title);
    setProp("og:description", description);
    if (image) setProp("og:image", image);
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
    // Remove all previously injected JSON-LD blocks
    document.querySelectorAll('script[data-page-jsonld="true"]').forEach((n) => n.remove());
    const existingLegacy = document.getElementById("page-jsonld");
    if (existingLegacy) existingLegacy.remove();
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((entry, i) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-page-jsonld", "true");
        s.id = i === 0 ? "page-jsonld" : `page-jsonld-${i}`;
        s.textContent = JSON.stringify(entry);
        document.head.appendChild(s);
      });
    }
  }, [title, description, canonical, image, jsonLd]);
};
